import React, { useState } from "react"
import { DatePicker, TimePicker, Radio, ConfigProvider, message } from "antd"
import enUS from "antd/es/locale/en_US"
import { CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons"
import { translations } from "../../../utils/Translation/staticTranslation"
import { getUserTimezone } from "../../../utils/timezoneUtils"
import dayjs from "dayjs"

const ScheduleStep = ({
  formData,
  setFormData,
  viewMode = false,
  showScheduleError = false,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.campaignsPage
  const [scheduleType, setScheduleType] = useState(
    formData.scheduleType || null,
  )
  const [selectedDate, setSelectedDate] = useState(
    formData.scheduledDate ? dayjs(formData.scheduledDate) : null,
  )
  const defaultTime = dayjs("04:00 AM", "hh:mm A")
  const [selectedTime, setSelectedTime] = useState(
    formData.scheduledTime
      ? dayjs(formData.scheduledTime, "hh:mm A")
      : defaultTime,
  )

  // Sync local state with formData changes (especially when editing)
  React.useEffect(() => {
    const newScheduleType = formData.scheduleType || null
    if (newScheduleType !== scheduleType) {
      setScheduleType(newScheduleType)
    }

    // Sync selectedDate
    if (formData.scheduledDate) {
      const date = dayjs(formData.scheduledDate)
      if (!date.isValid()) {
        setSelectedDate(null)
      } else if (!selectedDate || !date.isSame(selectedDate, "day")) {
        setSelectedDate(date)
      }
    } else if (selectedDate && newScheduleType !== "scheduled") {
      setSelectedDate(null)
    }

    // Sync selectedTime
    if (formData.scheduledTime) {
      const time = dayjs(formData.scheduledTime, "hh:mm A")
      if (!time.isValid()) {
        setSelectedTime(defaultTime)
      } else if (!selectedTime || !time.isSame(selectedTime, "minute")) {
        setSelectedTime(time)
      }
    } else if (newScheduleType === "scheduled" && !formData.scheduledTime) {
      if (!selectedTime || !selectedTime.isSame(defaultTime, "minute")) {
        setSelectedTime(defaultTime)
      }
    }
  }, [formData.scheduleType, formData.scheduledDate, formData.scheduledTime])

  // Initialize scheduledTime in formData if not set and scheduleType is "scheduled"
  React.useEffect(() => {
    if (scheduleType === "scheduled" && !formData.scheduledTime) {
      setFormData((prev) => ({
        ...prev,
        scheduledTime: defaultTime.format("HH:mm A"),
      }))
    }
  }, [scheduleType])

  const handleScheduleTypeChange = (e) => {
    const value = e.target.value
    setScheduleType(value)
    // Reset local state when changing schedule type
    if (value !== "scheduled") {
      setSelectedDate(null)
      setSelectedTime(defaultTime)
    } else {
      setSelectedDate(null)
      setSelectedTime(defaultTime)
    }
    setFormData((prev) => ({
      ...prev,
      scheduleType: value,
      scheduledDate: null,
      scheduledTime: null,
      // Always set timezone when schedule type changes
      scheduled_timezone: getUserTimezone(),
    }))
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)

    // If date is today, ensure the selected time is in the future
    if (date) {
      const now = dayjs()
      const today = dayjs().startOf("day")
      const selectedDateStart = date.startOf("day")

      if (selectedDateStart.isSame(today, "day") && selectedTime) {
        const selectedDateTime = date
          .hour(selectedTime.hour())
          .minute(selectedTime.minute())
          .second(0)
          .millisecond(0)

        // If selected time is in the past, reset to a future time (current time + 1 hour)
        if (selectedDateTime.isBefore(now)) {
          const futureTime = now.add(1, "hour").minute(0).second(0)
          setSelectedTime(futureTime)
          setFormData((prev) => ({
            ...prev,
            scheduledDate: date.format("YYYY-MM-DD"),
            scheduledTime: futureTime.format("HH:mm A"),
            scheduled_timezone: getUserTimezone(),
          }))
          return
        }
      }
    }

    setFormData((prev) => {
      const updates = {
        ...prev,
        scheduledDate: date ? date.format("YYYY-MM-DD") : null,
      }
      // If date is set and time is not set, set default time
      if (date && !prev.scheduledTime) {
        const now = dayjs()
        const today = dayjs().startOf("day")
        const selectedDateStart = date.startOf("day")

        // If today is selected, ensure default time is in the future
        if (selectedDateStart.isSame(today, "day")) {
          const futureTime = now.add(1, "hour").minute(0).second(0)
          updates.scheduledTime = futureTime.format("HH:mm A")
          setSelectedTime(futureTime)
        } else {
          updates.scheduledTime = defaultTime.format("HH:mm A")
        }
      }
      // Capture timezone when date is selected
      if (date) {
        updates.scheduled_timezone = getUserTimezone()
      }
      return updates
    })
  }

  const handleTimeChange = (time) => {
    if (time && selectedDate) {
      const now = dayjs()
      const selectedDateTime = selectedDate
        .hour(time.hour())
        .minute(time.minute())
        .second(0)
        .millisecond(0)

      if (selectedDateTime.isBefore(now)) {
        message.error(
          t?.schedule?.time_must_be_future ||
            "Selected time must be in the future. Please select a future time.",
        )
        return
      }
    }

    setSelectedTime(time)
    setFormData((prev) => {
      const updates = {
        ...prev,
        scheduledTime: time ? time.format("HH:mm A") : null,
      }
      // Capture timezone when time is selected
      if (time) {
        updates.scheduled_timezone = getUserTimezone()
      }
      return updates
    })
  }

  // Disable dates before today - allow today and future dates
  const disabledDate = (current) => {
    if (!current) return false
    const today = dayjs().startOf("day")
    const currentDate = current.startOf("day")

    return currentDate.isBefore(today, "day")
  }

  const disabledTime = () => {
    // If no date is selected, allow all times
    if (!selectedDate) {
      return {}
    }

    const now = dayjs()
    const today = dayjs().startOf("day")
    const selectedDateStart = selectedDate.startOf("day")

    // If selected date is today, disable past times
    if (selectedDateStart.isSame(today, "day")) {
      const currentHour = now.hour()
      const currentMinute = now.minute()

      return {
        disabledHours: () => {
          // Disable all hours before current hour
          const hours = []
          for (let i = 0; i < currentHour; i++) {
            hours.push(i)
          }
          return hours
        },
        disabledMinutes: (selectedHour) => {
          // If selected hour is current hour, disable minutes before current minute
          if (selectedHour === currentHour) {
            const minutes = []
            for (let i = 0; i < currentMinute; i++) {
              minutes.push(i)
            }
            return minutes
          }
          // For other hours, allow all minutes
          return []
        },
      }
    }

    // If selected date is in the future, allow all times
    return {}
  }

  const getScheduleDisplayText = () => {
    if (scheduleType === "immediately")
      return t?.schedule?.send_immediately || "Send Immediately"
    if (scheduleType === "scheduled") {
      if (selectedDate && selectedTime) {
        return `${selectedDate.format("DD/MM/YYYY")} at ${selectedTime.format(
          "hh:mm A",
        )}`
      }
      return t?.schedule?.schedule_for_later || "Schedule for Later"
    }
    if (scheduleType === "save")
      return t?.schedule?.save_for_later || "Save for Later"
    return t?.schedule?.not_scheduled || "Not scheduled"
  }

  return (
    <div className="schedule-step-container">
      <div className="schedule-section">
        <h3 className="schedule-section-title">
          {t?.schedule?.schedule_campaign || "Schedule Campaign"}
          {showScheduleError && (
            <span className="schedule-required-asterisk" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </h3>
        {showScheduleError && (
          <div className="schedule-error-alert" role="alert">
            {t?.schedule?.please_select || "Please select"}
          </div>
        )}

        <div className="schedule-options">
          {/* Send Immediately */}
          <div
            className={`schedule-option-card ${
              scheduleType === "immediately" ? "selected" : ""
            } ${viewMode ? "disabled" : ""}`}
            onClick={() =>
              !viewMode &&
              handleScheduleTypeChange({ target: { value: "immediately" } })
            }
            style={viewMode ? { cursor: "not-allowed", opacity: 0.6 } : {}}
          >
            <div className="schedule-option-content">
              <div className="schedule-option-title">
                {t?.schedule?.send_immediately || "Send Immediately"}
              </div>
              <div className="schedule-option-description">
                {t?.schedule?.send_immediately_description ||
                  "Send your campaign to recipients right away"}
              </div>
            </div>
            <Radio
              checked={scheduleType === "immediately"}
              onChange={() =>
                !viewMode &&
                handleScheduleTypeChange({ target: { value: "immediately" } })
              }
              disabled={viewMode}
            />
          </div>

          {/* Schedule for Later */}
          <div
            className={`schedule-option-card ${
              scheduleType === "scheduled" ? "selected" : ""
            } ${viewMode ? "disabled" : ""}`}
            onClick={() =>
              !viewMode &&
              handleScheduleTypeChange({ target: { value: "scheduled" } })
            }
            style={viewMode ? { cursor: "not-allowed", opacity: 0.6 } : {}}
          >
            <div className="schedule-option-content">
              <div className="schedule-option-title">
                {t?.schedule?.schedule_for_later || "Schedule for Later"}
              </div>
              <div className="schedule-option-description">
                {t?.schedule?.schedule_for_later_description ||
                  "Choose a date and time to send your campaign automatically"}
              </div>
            </div>
            <Radio
              checked={scheduleType === "scheduled"}
              onChange={() =>
                !viewMode &&
                handleScheduleTypeChange({ target: { value: "scheduled" } })
              }
              disabled={viewMode}
            />
          </div>

          {/* Date and Time inputs - shown when Schedule for Later is selected */}
          {scheduleType === "scheduled" && (
            <ConfigProvider locale={enUS} direction="ltr">
              <div
                className="schedule-datetime-fields"
                style={{ direction: "ltr" }}
              >
                <div className="schedule-field-group">
                  <label className="schedule-field-label">
                    {t?.schedule?.date || "Date"}{" "}
                    <span style={{ color: "#D92D20" }}>*</span>
                  </label>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    suffixIcon={<CalendarOutlined />}
                    className="schedule-date-input"
                    disabled={viewMode}
                    disabledDate={disabledDate}
                    showToday={true}
                    style={{ direction: "ltr" }}
                    getPopupContainer={(trigger) => {
                      const container =
                        trigger.closest(".schedule-datetime-fields") ||
                        document.body
                      return container
                    }}
                    popupClassName="schedule-date-picker-popup"
                    popupStyle={{ direction: "ltr" }}
                  />
                </div>
                <div className="schedule-field-group">
                  <label className="schedule-field-label">
                    {t?.schedule?.time || "Time"}{" "}
                    <span style={{ color: "#D92D20" }}>*</span>
                  </label>
                  <TimePicker
                    value={selectedTime}
                    onChange={handleTimeChange}
                    format="hh:mm A"
                    suffixIcon={<ClockCircleOutlined />}
                    className="schedule-time-input"
                    use12Hours
                    disabled={viewMode}
                    disabledTime={disabledTime}
                    style={{ direction: "ltr" }}
                    getPopupContainer={(trigger) => {
                      const container =
                        trigger.closest(".schedule-datetime-fields") ||
                        document.body
                      return container
                    }}
                    popupClassName="schedule-time-picker-popup"
                    popupStyle={{ direction: "ltr" }}
                  />
                </div>
              </div>
            </ConfigProvider>
          )}

          {/* Save for Later */}
          <div
            className={`schedule-option-card ${
              scheduleType === "save" ? "selected" : ""
            } ${viewMode ? "disabled" : ""}`}
            onClick={() =>
              !viewMode &&
              handleScheduleTypeChange({ target: { value: "save" } })
            }
            style={viewMode ? { cursor: "not-allowed", opacity: 0.6 } : {}}
          >
            <div className="schedule-option-content">
              <div className="schedule-option-title">
                {t?.schedule?.save_for_later || "Save for Later"}
              </div>
              <div className="schedule-option-description">
                {t?.schedule?.save_for_later_description ||
                  "Save your campaign to review or edit later"}
              </div>
            </div>
            <Radio
              checked={scheduleType === "save"}
              onChange={() =>
                !viewMode &&
                handleScheduleTypeChange({ target: { value: "save" } })
              }
              disabled={viewMode}
            />
          </div>
        </div>
      </div>

      {/* Campaign Summary */}
      <div className="campaign-summary-section">
        <h3 className="campaign-summary-title">
          {t?.schedule?.campaign_summary || "Campaign Summary"}
        </h3>
        <div className="campaign-summary-content">
          <div className="campaign-summary-column">
            <div className="summary-item">
              <span className="summary-label">
                {t?.schedule?.summary?.title || "Title:"}
              </span>
              <span className="summary-value">
                {formData.title || t?.schedule?.summary?.na || "N/A"}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">
                {t?.schedule?.summary?.audience || "Audience:"}
              </span>
              <span className="summary-value">
                {(() => {
                  const parts = []
                  if (formData.file_path) {
                    parts.push(t?.audience?.excel_sheet || "Excel Sheet")
                  }
                  if (formData.audiencePresets?.length) {
                    parts.push(...formData.audiencePresets)
                  }
                  if (formData.selectedUsers?.length) {
                    parts.push(
                      `${t?.audience?.custom_users || "Custom Users"} (${formData.selectedUsers.length})`,
                    )
                  }
                  if (formData.selectedContactUsers?.length) {
                    parts.push(
                      `${t?.audience?.contact_users || "Contact Users"} (${formData.selectedContactUsers.length})`,
                    )
                  }
                  if (formData.selectedContactGroups?.length) {
                    parts.push(
                      `${t?.audience?.contact_groups || "Contact Groups"} (${formData.selectedContactGroups.length})`,
                    )
                  }
                  if (parts.length) return parts.join(", ")
                  if (formData.audience === "Custom Users") {
                    return t?.audience?.custom_users || "Custom Users"
                  }
                  return formData.audience || t?.schedule?.summary?.na || "N/A"
                })()}
              </span>
            </div>
          </div>
          <div className="campaign-summary-column">
            <div className="summary-item">
              <span className="summary-label">
                {t?.schedule?.summary?.type || "Type:"}
              </span>
              <span className="summary-value">
                {formData.campaignType || t?.schedule?.summary?.na || "N/A"}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">
                {t?.schedule?.summary?.schedule || "Schedule:"}
              </span>
              <span className="summary-value">{getScheduleDisplayText()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScheduleStep
